/**
 * Offline Sync Service
 * Multi-step resumable protocol for syncing offline verification sessions.
 *
 * Steps:
 *  1. Auth check
 *  2. Upload photos one-by-one (resumable)
 *  3. Upload voice notes (resumable)
 *  4. Submit session bundle JSON
 *  5. Handle server response
 */

import api from './api';
import indexedDBHelper from '../utils/indexedDBHelper';
import offlineCameraService from './offlineCameraService';
import {
  getPendingSessions,
  markSyncing,
  markSyncFailed,
  updateSyncState,
  getSession
} from './offlineVerificationSession';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const MAX_RETRIES = 3;
const BACKOFF_BASE_MS = 2000;

class OfflineSyncService {
  constructor() {
    this.syncing = false;
    this.listeners = [];
  }

  /**
   * Register a listener for sync status updates
   */
  onStatusChange(callback) {
    this.listeners.push(callback);
    return () => { this.listeners = this.listeners.filter(cb => cb !== callback); };
  }

  _notify(event) {
    this.listeners.forEach(cb => { try { cb(event); } catch {} });
  }

  /**
   * Sync all pending sessions
   */
  async syncAllPending() {
    if (this.syncing) return { success: false, message: 'Sync already in progress' };
    if (!navigator.onLine) return { success: false, message: 'Device is offline' };

    this.syncing = true;
    this._notify({ type: 'sync_started' });

    const sessions = await getPendingSessions();
    const results = [];

    for (const session of sessions) {
      try {
        const result = await this.syncSession(session.sessionId);
        results.push({ sessionId: session.sessionId, ...result });
      } catch (err) {
        results.push({ sessionId: session.sessionId, success: false, error: err.message });
      }
    }

    this.syncing = false;
    this._notify({ type: 'sync_completed', results });
    return { success: true, results };
  }

  /**
   * Sync a single session (multi-step, resumable)
   */
  async syncSession(sessionId) {
    const session = await getSession(sessionId);
    if (!session) throw new Error(`Session ${sessionId} not found`);
    if (session.status === 'synced') return { success: true, message: 'Already synced' };

    await markSyncing(sessionId);
    this._notify({ type: 'session_syncing', sessionId });

    try {
      // Step 1: Auth check
      await this._checkAuth();

      // Step 2: Upload photos
      const currentSession = await getSession(sessionId);
      const uploadedPhotoIds = new Set(currentSession.sync.uploadedPhotoIds || []);
      const photoManifest = [];

      for (const photoRef of currentSession.photos) {
        if (uploadedPhotoIds.has(photoRef.photoId)) {
          // Already uploaded in a previous attempt
          const photo = await indexedDBHelper.getPhoto(photoRef.photoId);
          photoManifest.push({
            photoId: photoRef.photoId,
            serverUrl: photo?.serverUrl || null,
            serverId: photo?.serverId || null,
            hash: photoRef.hash,
            capturedAt: photoRef.capturedAt,
            location: photoRef.location,
            checklistItemId: photoRef.checklistItemId
          });
          continue;
        }

        const result = await this._retryUpload(() => offlineCameraService.uploadPhoto(photoRef.photoId));
        uploadedPhotoIds.add(photoRef.photoId);

        photoManifest.push({
          photoId: photoRef.photoId,
          serverUrl: result?.data?.url || null,
          serverId: result?.data?.id || null,
          hash: photoRef.hash,
          capturedAt: photoRef.capturedAt,
          location: photoRef.location,
          checklistItemId: photoRef.checklistItemId
        });

        // Persist progress
        await updateSyncState(sessionId, { uploadedPhotoIds: [...uploadedPhotoIds] });
        this._notify({ type: 'photo_uploaded', sessionId, photoId: photoRef.photoId, total: currentSession.photos.length, done: uploadedPhotoIds.size });
      }

      // Step 3: Upload voice notes
      const uploadedVoiceNoteIds = new Set(currentSession.sync.uploadedVoiceNoteIds || []);
      const voiceNoteManifest = [];

      for (const vnRef of currentSession.voiceNotes) {
        if (uploadedVoiceNoteIds.has(vnRef.id)) {
          voiceNoteManifest.push({ ...vnRef });
          continue;
        }

        const vnData = await indexedDBHelper.getVoiceNote(vnRef.id);
        if (!vnData?.blob) {
          console.warn(`Voice note ${vnRef.id} blob not found, skipping`);
          continue;
        }

        const result = await this._retryUpload(async () => {
          const blob = new Blob([vnData.blob], { type: vnData.mimeType || 'audio/webm' });
          const formData = new FormData();
          formData.append('audio', blob, `${vnRef.id}.webm`);
          formData.append('metadata', JSON.stringify({
            verificationId: currentSession.verificationId,
            sessionId: currentSession.sessionId,
            contentHash: vnData.contentHash,
            location: vnData.location,
            durationSeconds: vnData.durationSeconds,
            capturedAt: vnData.capturedAt,
            checklistItemId: vnData.checklistItemId
          }));

          return api.post(`${API_BASE}/uploads/verification-voice-notes`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        });

        uploadedVoiceNoteIds.add(vnRef.id);
        voiceNoteManifest.push({
          ...vnRef,
          serverUrl: result?.data?.data?.url || null,
          serverId: result?.data?.data?.id || null
        });

        await updateSyncState(sessionId, { uploadedVoiceNoteIds: [...uploadedVoiceNoteIds] });
        this._notify({ type: 'voice_note_uploaded', sessionId, noteId: vnRef.id });
      }

      // Step 4: Submit session bundle (JSON only, no blobs)
      const bundle = {
        sessionId: currentSession.sessionId,
        verificationId: currentSession.verificationId,
        verifierId: currentSession.verifierId,
        verificationTier: currentSession.verificationTier,

        preflight: {
          cachedAt: currentSession.preflight.cachedAt,
          serverTimeAtCache: currentSession.preflight.serverTimeAtCache
        },

        geofenceProofs: currentSession.geofenceProofs,
        photoManifest,
        voiceNoteManifest,

        checklistState: currentSession.checklistState,
        verifierNotes: currentSession.verifierNotes,
        draftReport: currentSession.draftReport,

        signature: currentSession.signature,

        startedAt: currentSession.startedAt,
        completedAt: currentSession.completedAt,

        integrity: currentSession.integrity,

        deviceInfo: currentSession.integrity?.deviceInfo
      };

      const response = await api.post(
        `${API_BASE}/simple-verifications/${currentSession.verificationId}/submit-offline-session`,
        bundle
      );

      const serverResult = response.data;

      if (serverResult.accepted) {
        await updateSyncState(sessionId, {
          serverAcknowledged: true,
          serverSessionId: serverResult.serverSessionId
        });
        this._notify({ type: 'session_synced', sessionId, serverResult });
        return { success: true, serverResult };
      } else {
        await markSyncFailed(sessionId, serverResult.reason || 'Server rejected session');
        this._notify({ type: 'session_rejected', sessionId, reason: serverResult.reason });
        return { success: false, reason: serverResult.reason };
      }

    } catch (err) {
      await markSyncFailed(sessionId, err.message);
      this._notify({ type: 'session_sync_failed', sessionId, error: err.message });
      throw err;
    }
  }

  /**
   * Retry an upload function with exponential backoff
   */
  async _retryUpload(uploadFn) {
    let lastError;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        return await uploadFn();
      } catch (err) {
        lastError = err;
        if (attempt < MAX_RETRIES - 1) {
          const delay = BACKOFF_BASE_MS * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    throw lastError;
  }

  /**
   * Check if auth is still valid
   */
  async _checkAuth() {
    try {
      await api.get(`${API_BASE}/auth/me`);
    } catch (err) {
      if (err.response?.status === 401) {
        throw new Error('AUTH_EXPIRED: Please log in again before syncing.');
      }
      throw err;
    }
  }

  /**
   * Get pending session count
   */
  async getPendingCount() {
    const sessions = await getPendingSessions();
    return sessions.length;
  }
}

const offlineSyncService = new OfflineSyncService();

// Setup background sync on online event
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    offlineSyncService.syncAllPending().catch(err => {
      console.error('Auto-sync failed:', err);
    });
  });
}

export default offlineSyncService;
